/** @jsxFrag React.Fragment */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';

import has from 'lodash/has';
import { branch, compose, mapProps } from 'recompose';

import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import type { Theme } from '@coursera/cds-core';
import { Button, Hidden, Tab, Tabs, typography2, useTheme } from '@coursera/cds-core';

import withHasFinancialAidApplications from 'bundles/my-purchases-graphql/components/enhancers/withHasFinancialAidApplications';
import type { TabNames } from 'bundles/my-purchases-graphql/components/utils/fetchTabnameByLocation';
import { getTabNames } from 'bundles/my-purchases-graphql/components/utils/fetchTabnameByLocation';
import Naptime from 'bundles/naptimejs';
import SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';

import _t from 'i18n!nls/my-purchases-graphql';

type PropsFromCaller = {
  children?: ReactNode;
  currentTab: string;
};

type PropsFromNaptime = {
  hasSubscriptions: boolean;
  hasFinancialAidApplications: boolean;
};

export type Props = PropsFromCaller & PropsFromNaptime;

const useStyles = (theme: Theme) => ({
  buttonTabDefault: css({
    '.cds-button-label': {
      ...typography2.bodyPrimary,
      color: 'var(--cds-color-neutral-primary)',
      ':hover': {
        color: `var(--cds-color-blue-800) !important`,
      },
    },
    ':hover': {
      textDecoration: 'none !important',
    },
  }),
  buttonTabSelected: css({
    '.cds-button-label': {
      ...typography2.subtitleMedium,
      color: 'var(--cds-color-blue-800)',
    },
  }),
  buttonTabContainer: css({}),
});

export const MyPurchasesTabs = ({ children, currentTab, hasSubscriptions, hasFinancialAidApplications }: Props) => {
  const router = useRouter();
  const tabNames = useMemo(() => getTabNames(), []);
  const theme = useTheme();
  const styles = useStyles(theme);

  useEffect(() => {
    if (!user?.isAuthenticatedUser()) return;

    const redirectToDefaultTab = () => {
      router?.replace({ pathname: '/my-purchases/transactions' });
    };

    switch (router?.location.pathname) {
      case '/my-purchases/financial-aid': {
        if (!hasFinancialAidApplications || !has(tabNames, 'financial-aid')) {
          redirectToDefaultTab();
        }
        break;
      }

      case '/my-purchases/subscriptions':
      default: {
        if (!hasSubscriptions || !has(tabNames, 'subscriptions')) {
          redirectToDefaultTab();
        }
      }
    }
  }, [hasSubscriptions, hasFinancialAidApplications, router, tabNames]);
  const filteredTabNames = Object.keys(tabNames).filter(
    (tabName) =>
      !(
        (!hasFinancialAidApplications && tabName === 'financial-aid') ||
        (!hasSubscriptions && tabName === 'subscriptions')
      )
  );

  const tabs = filteredTabNames.map((tabName) => (
    <Tab label={tabNames[tabName as keyof TabNames]} value={tabName} key={tabName} />
  ));

  const stackedTabs = filteredTabNames.map((tabName) => (
    <div
      css={[tabName === currentTab && css({ borderLeft: `solid 4px var(--cds-color-blue-700)`, marginLeft: '-4px' })]}
    >
      <Button
        component="button"
        variant="ghost"
        size="small"
        key={tabName}
        onClick={() => {
          if (tabName === currentTab) return;
          router.push({
            pathname: `/my-purchases/${tabName}`,
          });
        }}
        css={[styles.buttonTabDefault, tabName === currentTab && styles.buttonTabSelected]}
      >
        {tabNames[tabName as keyof TabNames]}
      </Button>
    </div>
  ));
  return (
    <>
      <Hidden mdUp>
        <div css={css({ borderLeft: `solid 1px var(--cds-color-grey-50)` })}>{stackedTabs}</div>
      </Hidden>
      <Hidden smDown css={css({ width: '100%' })}>
        <Tabs
          onChange={(nextTab) => {
            if (nextTab === currentTab) return;

            router.push({
              pathname: `/my-purchases/${nextTab}`,
            });
          }}
          style={{ width: '100%', borderBottomColor: 'var(--cds-color-neutral-stroke-primary-weak)' }}
          aria-label={_t('My purchases tabs')}
        >
          {tabs}
        </Tabs>
      </Hidden>
      {children}
    </>
  );
};

export default compose<Props, PropsFromCaller>(
  branch(
    () => user.isAuthenticatedUser(),
    compose<Props, PropsFromCaller>(
      Naptime.createContainer(() => ({
        subscriptions: SubscriptionsV1.me({
          fields: ['id'],
        }),
      })),
      mapProps((props: PropsFromCaller & { subscriptions: SubscriptionsV1[] }) => ({
        ...props,
        hasSubscriptions: Boolean(props.subscriptions?.length),
      })),
      withHasFinancialAidApplications
    )
  )
)(MyPurchasesTabs);
